import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Paper, Typography, Button, CircularProgress, Fade } from '@mui/material';
import { ArrowBack, LockOutlined, ErrorOutline } from '@mui/icons-material';
import { getUserIdFromToken } from '../utils/Auth';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ─── Design Tokens — matches Login.js ───────────────────────────────────── */
const T = {
  amber:      '#C2823C',
  amberLight: '#E8A85A',
  cream:      '#FAF8F4',
  ivory:      '#FFFEFB',
  ink:        '#1a1510',
  inkMid:     '#4a3f30',
  inkLight:   '#8a7055',
  border:     'rgba(194,130,60,0.18)',
  shadow:     '0 20px 60px rgba(100,70,30,0.12)',
  error:      '#d32f2f',
};

const AccessVideo = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | ok | needsLogin | denied | error
  const [videoUrl, setVideoUrl] = useState(null);
  const [eventName, setEventName] = useState('');

  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) { setStatus('needsLogin'); return; }
      try {
        const r = await axios.get(`${BASE_URL}/video/share/${shareId}`, {
          headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
        });
        if (r.data?.success) {
          setVideoUrl(r.data.video_url);
          setEventName(r.data.event_name || '');
          setStatus('ok');
        } else {
          setStatus('denied');
        }
      } catch (err) {
        if (err.response?.status === 401) setStatus('needsLogin');
        else if (err.response?.status === 403) setStatus('denied');
        else setStatus('error');
      }
    };
    check();
  }, [shareId]);

  /* ─── Shared page chrome ──────────────────────────────────────────────── */
  const Wrapper = ({ children }) => (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: T.cream,
      position: 'relative',
      overflow: 'hidden',
      py: 6,
      px: 2,
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(194,130,60,0.05) 0%, transparent 40%),
                          radial-gradient(circle at 80% 70%, rgba(194,130,60,0.05) 0%, transparent 40%)`,
        pointerEvents: 'none',
      },
    }}>
      {/* Back to dashboard */}
      <Box sx={{ width: '100%', maxWidth: 900, position: 'relative', zIndex: 1, mb: 3 }}>
        <Button
          onClick={() => {
    const userId = localStorage.getItem('userId') || getUserIdFromToken();
    navigate('/DashBoard', { state: { userId } });
  }}
          startIcon={<ArrowBack />}
          sx={{
            textTransform: 'none',
            color: T.inkMid,
            fontWeight: 600,
            borderRadius: '50px',
            px: 2,
            '&:hover': { background: 'rgba(194,130,60,0.08)', color: T.amber },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Wordmark */}
      <Box sx={{ mb: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Typography variant="h4" sx={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 600,
          letterSpacing: '0.1em',
          color: T.ink,
        }}>
          VIMOIR
        </Typography>
      </Box>

      {children}
    </Box>
  );

  const StatusCard = ({ icon, title, body, action }) => (
    <Fade in timeout={600}>
      <Paper elevation={0} sx={{
        background: T.ivory,
        border: `1px solid ${T.border}`,
        borderRadius: '32px',
        padding: { xs: 5, sm: 6 },
        width: '100%',
        maxWidth: 450,
        boxShadow: T.shadow,
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 3,
          background: `linear-gradient(135deg, ${T.amber}22, ${T.amberLight}22)`,
          border: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Typography variant="h5" sx={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 600,
          color: T.ink,
          mb: 1.5,
        }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: T.inkLight, mb: action ? 4 : 0, lineHeight: 1.7 }}>
          {body}
        </Typography>
        {action}
      </Paper>
    </Fade>
  );

  /* ─── States ──────────────────────────────────────────────────────────── */
  if (status === 'checking') {
    return (
      <Wrapper>
        <StatusCard
          icon={<CircularProgress size={28} sx={{ color: T.amber }} />}
          title="Checking access"
          body="One moment while we verify your invitation…"
        />
      </Wrapper>
    );
  }

  if (status === 'needsLogin') {
    return (
      <Wrapper>
        <StatusCard
          icon={<LockOutlined sx={{ color: T.amber, fontSize: 28 }} />}
          title="Sign in to continue"
          body="This memory was shared with a specific account. Log in to view it."
          action={
            <Button
              onClick={() => navigate(`/login?redirect=/access-video/${shareId}`)}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '50px',
                padding: '12px 0',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`,
                boxShadow: '0 8px 20px rgba(194,130,60,0.25)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${T.amberLight} 0%, ${T.amber} 100%)`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Log In
            </Button>
          }
        />
      </Wrapper>
    );
  }

  if (status === 'denied') {
    return (
      <Wrapper>
        <StatusCard
          icon={<LockOutlined sx={{ color: T.error, fontSize: 28 }} />}
          title="Access not granted"
          body="This video wasn't shared with your account. Ask the organizer to add your email if you believe this is a mistake."
        />
      </Wrapper>
    );
  }

  if (status === 'error') {
    return (
      <Wrapper>
        <StatusCard
          icon={<ErrorOutline sx={{ color: T.error, fontSize: 28 }} />}
          title="Something went wrong"
          body="We couldn't load this video right now. Please try again in a moment."
          action={
            <Button
              onClick={() => window.location.reload()}
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: '50px',
                padding: '12px 0',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderColor: T.border,
                color: T.inkMid,
                '&:hover': { borderColor: T.amber, color: T.amber, background: 'rgba(194,130,60,0.06)' },
              }}
            >
              Try Again
            </Button>
          }
        />
      </Wrapper>
    );
  }

  /* ─── Success — video player ──────────────────────────────────────────── */
  return (
    <Wrapper>
      <Fade in timeout={600}>
        <Box sx={{
          width: '100%',
          maxWidth: 900,
          background: T.ivory,
          border: `1px solid ${T.border}`,
          borderRadius: '28px',
          boxShadow: T.shadow,
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
        }}>
          <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 2 }}>
            <Typography sx={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 600,
              fontSize: '1.6rem',
              color: T.ink,
            }}>
              {eventName || 'Shared Memory'}
            </Typography>
            <Typography variant="body2" sx={{ color: T.inkLight, fontStyle: 'italic', mt: 0.3 }}>
              Shared with you on Vimoir
            </Typography>
          </Box>
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 4 } }}>
            <Box sx={{ borderRadius: '16px', overflow: 'hidden', background: '#111' }}>
              <video
                controls
                controlsList="nodownload noremoteplayback"
                style={{ width: '100%', display: 'block', maxHeight: '70vh' }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Wrapper>
  );
};

export default AccessVideo;